import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Landmark, Search } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { DisclosureNotice } from "@/components/PayByBank";
import { EmptyState, ErrorState, LoadingState } from "@/components/PageUi";
import { apiErrorMessage } from "@/lib/api-error";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import type { components } from "@dolfin/api-admin";

type BankInstitution = components["schemas"]["BankInstitutionResponse"];
type Disclosure = components["schemas"]["BankConnectionDisclosureResponse"];

// Past this many banks a scrolling list stops being scannable, so a search box appears above it.
const BANK_SEARCH_THRESHOLD = 8;

/** Where the bank sends the account holder back to (via the backend callback). */
export function bankConnectionReturnUrl() {
  return `${window.location.origin}/organisation/bank-connections/return`;
}

export function ConnectBankDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const api = useDolfinClient();
  const [institutions, setInstitutions] = useState<BankInstitution[]>([]);
  const [disclosure, setDisclosure] = useState<Disclosure | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [bankQuery, setBankQuery] = useState("");

  const fetchInstitutions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await api.GET("/v1/bank-connections/institutions");
      if (error || !data) {
        setLoadError(apiErrorMessage(error, "Failed to load available banks"));
        return;
      }
      setInstitutions(data.institutions);
      setDisclosure(data.disclosure);
      if (data.institutions.length === 1) setSelectedId(data.institutions[0]!.id);
    } catch {
      setLoadError("Failed to load available banks");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) {
      setBankQuery("");
      fetchInstitutions();
    }
  }, [open, fetchInstitutions]);

  const query = bankQuery.trim().toLowerCase();
  const visible = query ? institutions.filter((i) => i.name.toLowerCase().includes(query)) : institutions;
  // A bank the search has hidden is no longer a visible choice, so it cannot be the one connected.
  const chosenId = visible.some((i) => i.id === selectedId) ? selectedId : null;

  async function handleContinue() {
    if (!chosenId || starting) return;
    setStarting(true);
    try {
      const { data, error } = await api.POST("/v1/bank-connections", {
        body: { institutionId: chosenId, returnUrl: bankConnectionReturnUrl() },
      });
      if (error || !data?.authorisationUrl) {
        toast.error(apiErrorMessage(error, "Failed to start connecting the bank"));
        setStarting(false);
        return;
      }
      // Off to the bank; navigating away, so the button stays disabled.
      window.location.assign(data.authorisationUrl);
    } catch {
      toast.error("Failed to start connecting the bank");
      setStarting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !starting && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="size-5" />
            Connect a bank
          </DialogTitle>
          <DialogDescription>
            Give read-only access to your business accounts so their balances appear here. You&apos;ll be
            securely redirected to your bank to approve it. Nothing can be paid from this connection.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <LoadingState label="Loading banks..." />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchInstitutions} />
        ) : institutions.length === 0 ? (
          <EmptyState icon={Landmark} title="No banks available">
            Bank connections are not available right now.
          </EmptyState>
        ) : (
          <div className="space-y-2">
            <Label className="text-xs" htmlFor={institutions.length > BANK_SEARCH_THRESHOLD ? "connect-bank-search" : undefined}>
              Choose your bank
            </Label>
            {institutions.length > BANK_SEARCH_THRESHOLD ? (
              <div className="relative">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="connect-bank-search"
                  value={bankQuery}
                  onChange={(e) => setBankQuery(e.target.value)}
                  placeholder="Search banks"
                  className="h-9 pl-8"
                />
              </div>
            ) : null}
            <div className="max-h-64 space-y-1.5 overflow-auto pr-1">
              {visible.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No bank matches that name.</p>
              ) : null}
              {visible.map((inst) => (
                <button
                  key={inst.id}
                  type="button"
                  aria-pressed={selectedId === inst.id}
                  onClick={() => setSelectedId(inst.id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition hover:bg-muted/60 ${
                    selectedId === inst.id ? "border-primary bg-primary/5" : ""
                  }`}
                >
                  {inst.iconUrl ? <img src={inst.iconUrl} alt="" className="size-6 shrink-0 object-contain" /> : null}
                  <span className="text-sm font-medium">{inst.name}</span>
                  {selectedId === inst.id && <CheckCircle2 className="ml-auto size-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>
        )}

        <DisclosureNotice disclosure={disclosure} />

        <DialogFooter>
          <Button variant="ghost" disabled={starting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!chosenId || starting || loading} onClick={handleContinue}>
            {starting ? <Spinner className="size-4" /> : <ExternalLink className="size-4" />}
            Continue to your bank
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
